import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiUrl } from '../config/api'
import { supabase } from '../config/supabase'

interface ProtectedRouteProps {
  children: React.ReactNode
  redirectTo?: string
  requireActiveSubscription?: boolean
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  redirectTo = '/login',
  requireActiveSubscription = false,
}) => {
  const { user, loading } = useAuth()
  const location = useLocation()
  const [checkingSub, setCheckingSub] = useState<boolean>(requireActiveSubscription)
  const [hasActiveSub, setHasActiveSub] = useState<boolean>(!requireActiveSubscription)

  // Optionally enforce active subscription (must call hooks unconditionally)
  useEffect(() => {
    let cancelled = false
    const check = async () => {
      if (!requireActiveSubscription) {
        setHasActiveSub(true)
        return
      }
      setCheckingSub(true)
      try {
        // Strategy 1: If we have a recent subscriptionId from onboarding, prefer it
        const subId = typeof window !== 'undefined' ? localStorage.getItem('subscriptionId') : null
        if (subId) {
          const start = Date.now()
          while (!cancelled && Date.now() - start < 12000) {
            try {
              const r = await fetch(apiUrl(`/api/subscription/${subId}`))
              if (r.ok) {
                const s = await r.json()
                const st = (s?.status || '').toLowerCase()
                if (st === 'active' || st === 'trialing') {
                  setHasActiveSub(true)
                  // Clear temporary onboarding artifact
                  try { localStorage.removeItem('subscriptionId') } catch (_) {}
                  return
                }
              }
            } catch (_) {}
            await new Promise(res => setTimeout(res, 800))
          }
        }

        // Strategy 2: Unified status endpoint by email
        try {
          const resp = user?.email
            ? await fetch(apiUrl(`/api/subscription-status?email=${encodeURIComponent(user.email)}`))
            : null
          if (resp && resp.ok) {
            const data = await resp.json()
            const status = (data?.status || '').toLowerCase()
            if (status === 'active' || status === 'trialing') {
              setHasActiveSub(true)
              return
            }
          }
        } catch (_) {}

        // Strategy 3: Check Supabase user metadata (updated by webhook)
        try {
          const { data } = await supabase.auth.getUser()
          const meta = data?.user?.user_metadata as any
          const st = (meta?.subscription_status || '').toLowerCase()
          if (st === 'active' || st === 'trialing') {
            setHasActiveSub(true)
            return
          }
        } catch (_) {}

        setHasActiveSub(false)
      } catch (e) {
        setHasActiveSub(false)
      } finally {
        if (!cancelled) setCheckingSub(false)
      }
    }
    check()
    return () => { cancelled = true }
  }, [requireActiveSubscription, user?.email])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!user) {
    // If onboarding just finished, we may still be waiting for session propagation.
    // Avoid bouncing to onboarding if a recent subscriptionId exists.
    const subId = typeof window !== 'undefined' ? localStorage.getItem('subscriptionId') : null
    if (requireActiveSubscription && subId) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )
    }
    // Otherwise redirect to login/onboarding
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  if (requireActiveSubscription) {
    if (checkingSub) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )
    }
    if (!hasActiveSub) {
      return <Navigate to={redirectTo} state={{ from: location }} replace />
    }
  }

  return <>{children}</>
}

export default ProtectedRoute

